import { code128, qrcode } from '@bwip-js/browser'
import { format } from 'date-fns'

import { sleep } from '@last/core'

import { useMoney } from '@/composables/useMoney'
import { t } from '@/i18n'
import images from '@/images'
import type { Company as CompanyType } from '@/types/company'
import type { TabComboProduct, TabModifier, TabProduct } from '@/types/tabProduct'

import type { KitchenComboFormatted, KitchenProductFormatted } from './kitchenOrderFormatter'

const BODY_FONT = 'Nunito Sans'
const TITLE_FONT = 'Poppins'

;(async function preloadFonts() {
  if (!document.fonts) return

  await document.fonts.ready

  const fonts = [
    '400 16px "Nunito Sans"',
    '700 16px "Nunito Sans"',
    '400 16px "Poppins"',
    '700 16px "Poppins"'
  ]

  const loadPromises = fonts.map(font => document.fonts.load(font))

  await Promise.all(loadPromises)
})()

function splitLines(
  ctx: CanvasRenderingContext2D,
  text: string | number | undefined,
  maxWidth: number,
  firstMaxWidth?: number
): string[] {
  if (!text) return []
  const words = String(text).split(' ')
  let line = ''
  const lines = []
  let currentMaxWidth = Math.max(firstMaxWidth ?? 0, 0) || maxWidth

  for (let i = 0; i < words.length; i++) {
    let test = words[i]
    let metrics = ctx.measureText(test)
    while (metrics.width > currentMaxWidth) {
      // Determine how much of the word will fit
      test = test.substring(0, test.length - 1)
      metrics = ctx.measureText(test)
    }
    if (words[i] != test) {
      words.splice(i + 1, 0, words[i].substr(test.length))
      words[i] = test
    }

    test = line + words[i] + ' '
    metrics = ctx.measureText(test)
    if (metrics.width > currentMaxWidth && i > 0) {
      lines.push(line)
      currentMaxWidth = maxWidth
      line = words[i] + ' '
    } else {
      line = test
    }
  }

  lines.push(line)
  return lines
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string | number | undefined,
  x: number,
  y: number,
  maxWidth: number,
  {
    lineHeight = 22,
    decreaseIndent = false,
    strike = false,
    firstMaxWidth
  }: {
    lineHeight?: number
    decreaseIndent?: boolean
    strike?: boolean
    firstMaxWidth?: number
  } = {}
): number {
  const lines = splitLines(ctx, text, maxWidth, firstMaxWidth)
  for (let i = 0; i < lines.length; ++i) {
    if (i > 0 && decreaseIndent) {
      x = 0
    }
    ctx.fillText(lines[i], x, y)
    if (strike) {
      const metrics = ctx.measureText(lines[i])
      ctx.fillRect(x, y - lineHeight / 2, metrics.width, 2)
    }
    y += lineHeight
  }
  return lines.length
}

function formatDate(date: string | number | Date | undefined): string {
  return format(new Date(date ?? Date.now()), 'dd/MM/yyyy HH:mm')
}

function formatDateWithoutTime(date: string | number | Date | undefined): string {
  return format(new Date(date ?? Date.now()), 'dd/MM/yyyy')
}

function formatCurrency(value: number): string {
  const { currency } = useMoney()
  return currency(value)
}

export interface TicketElement {
  height(ctx: CanvasRenderingContext2D, width: number): number | Promise<number>
  render(top: number, ctx: CanvasRenderingContext2D, width: number): void
}

export class Logo implements TicketElement {
  image: string
  ratio: number
  imageNode?: HTMLImageElement | null
  imageHeight?: number

  constructor(image: string, { ratio }: { ratio?: number } = {}) {
    this.image = image
    this.ratio = ratio || 1
  }

  async loadImage(width: number): Promise<number> {
    this.imageNode = await Promise.race([
      images.getImage(this.image),
      sleep(2_000).then(() => null)
    ])

    if (this.imageNode) {
      this.imageHeight = (this.imageNode.height / this.imageNode.width) * width * this.ratio
    } else {
      this.imageHeight = 0
    }

    return this.imageHeight
  }

  async height(ctx: CanvasRenderingContext2D, width: number): Promise<number> {
    if (!this.image || this.imageHeight === 0) return 0
    if (this.imageHeight) return this.imageHeight
    return await this.loadImage(width)
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    if (!this.imageHeight) return
    const ratio = this.ratio
    const height = this.imageHeight
    ctx.drawImage(
      this.imageNode as HTMLImageElement,
      width * 0.5 * (1 - ratio),
      top,
      width * ratio,
      height
    )
  }
}

export class CustomImage implements TicketElement {
  image: string
  ratio: number
  imageNode?: HTMLImageElement | null
  imageHeight?: number

  constructor(image: string, { ratio }: { ratio?: number } = {}) {
    this.image = image
    this.ratio = ratio || 1
  }

  async loadImage(width: number): Promise<number> {
    const processImage = () =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => {
          resolve(image)
        }
        image.onerror = reject
        image.src = this.image
      })

    this.imageNode = await processImage()

    if (this.imageNode) {
      this.imageHeight = (this.imageNode.height / this.imageNode.width) * width * this.ratio
    } else {
      this.imageHeight = 0
    }

    return this.imageHeight
  }

  async height(ctx: CanvasRenderingContext2D, width: number): Promise<number> {
    if (!this.image || this.imageHeight === 0) return 0
    if (this.imageHeight) return this.imageHeight
    return await this.loadImage(width)
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    if (!this.imageHeight) return
    const ratio = this.ratio
    const height = this.imageHeight
    ctx.drawImage(
      this.imageNode as HTMLImageElement,
      width * 0.5 * (1 - ratio),
      top,
      width * ratio,
      height
    )
  }
}

export class AdditionalInfo implements TicketElement {
  lineHeight: number
  topMargin: number
  additionalInfo?: string

  constructor({ ticketInfo }: { ticketInfo?: string }) {
    this.lineHeight = 22
    this.topMargin = 20
    this.additionalInfo = ticketInfo
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    if (!this.additionalInfo) return 0
    ctx.font = `14px ${BODY_FONT}`
    const lines = splitLines(ctx, this.additionalInfo, (2 / 3) * width).length
    return this.lineHeight * lines + this.topMargin
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    if (!this.additionalInfo) return
    top += this.topMargin
    ctx.font = `14px ${BODY_FONT}`
    ctx.textAlign = 'center'
    ctx.fillStyle = 'black'
    const lines = wrapText(ctx, this.additionalInfo, width / 2, top, (2 / 3) * width)
    top += this.lineHeight * lines
  }
}

export class Company implements TicketElement {
  company: CompanyType
  lineHeight: number
  nameMargin: number
  topMargin: number

  constructor(company: CompanyType) {
    this.company = company
    this.lineHeight = 22
    this.nameMargin = 10
    this.topMargin = 20
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    if (!this.company.name || !this.company.taxId) return 0
    ctx.font = `16px ${BODY_FONT}`
    const singleLines = 2
    const lines = singleLines + splitLines(ctx, this.company.address, width).length
    return this.lineHeight * lines + this.nameMargin + this.topMargin
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    if (!this.company.name || !this.company.taxId) return
    top += this.topMargin
    ctx.font = `bold 16px ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textAlign = 'center'
    ctx.fillText(this.company.name, width / 2, top)
    top += this.lineHeight + this.nameMargin
    ctx.font = `16px ${BODY_FONT}`
    ctx.fillText(this.company.taxId, width / 2, top)
    top += this.lineHeight
    wrapText(ctx, this.company.address, width / 2, top, width)
  }
}

export class Title implements TicketElement {
  text: string
  paddingTop: number
  paddingBottom: number
  boxHeight: number
  fontSize: number

  constructor(text: string, { fontSize, height }: { fontSize?: number; height?: number } = {}) {
    this.text = text
    this.paddingTop = 30
    this.paddingBottom = 20
    this.boxHeight = height || 60
    this.fontSize = fontSize || 30
  }

  height(): number {
    return this.paddingTop + this.boxHeight + this.paddingBottom
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    top += this.paddingTop
    ctx.fillStyle = 'black'
    ctx.fillRect(0, top, width, this.boxHeight)

    ctx.font = `bold ${this.fontSize}px ${TITLE_FONT}`
    if (ctx.measureText(this.text).width > width) {
      this.fontSize = Math.floor((this.fontSize * width) / ctx.measureText(this.text).width)
      ctx.font = `bold ${this.fontSize}px ${TITLE_FONT}`
    }

    ctx.fillStyle = 'white'
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.fillText(this.text, width / 2, top + this.boxHeight / 2)
  }
}

export class SubTitle implements TicketElement {
  text: string
  paddingTop: number
  paddingBottom: number
  fontSize: number

  constructor(text: string, { fontSize }: { fontSize?: number } = {}) {
    this.text = text
    this.paddingTop = 4
    this.paddingBottom = 4
    this.fontSize = fontSize || 20
  }

  height(): number {
    return this.paddingTop + this.fontSize + this.paddingBottom
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    top += this.paddingTop

    ctx.font = `bold ${this.fontSize}px ${BODY_FONT}`
    if (ctx.measureText(this.text).width > width) {
      this.fontSize = Math.floor((this.fontSize * width) / ctx.measureText(this.text).width)
      ctx.font = `bold ${this.fontSize}px ${BODY_FONT}`
    }

    ctx.fillStyle = 'black'
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.fillText(this.text, width / 2, top + this.fontSize / 2)
  }
}

type DoubleTitleOptions = {
  firstStyle?: string
  secondStyle?: string
  firstLineHeight?: number
  secondLineHeight?: number
}

export class DoubleTitle implements TicketElement {
  first: string
  second: string
  paddingTop: number
  paddingBottom: number
  boxHeight: number
  firstFont: string
  secondFont: string
  firstLineHeight: number
  secondLineHeight: number

  constructor(
    first: string,
    second: string,
    { firstStyle, secondStyle, firstLineHeight, secondLineHeight }: DoubleTitleOptions = {}
  ) {
    this.first = first
    this.second = second
    this.paddingTop = 30
    this.paddingBottom = 20
    this.firstLineHeight = firstLineHeight || 70
    this.secondLineHeight = secondLineHeight || 45
    this.boxHeight = this.firstLineHeight + this.secondLineHeight
    this.firstFont = `${firstStyle || 'bold 60px'} ${TITLE_FONT}`
    this.secondFont = `${secondStyle || '40px'} ${BODY_FONT}`
  }

  height(): number {
    return this.paddingTop + this.boxHeight + this.paddingBottom
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    top += this.paddingTop
    ctx.fillStyle = 'black'
    ctx.fillRect(0, top, width, this.boxHeight)
    ctx.fillStyle = 'white'
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.font = this.firstFont
    ctx.fillText(this.first, width / 2, top + this.firstLineHeight / 2)
    ctx.font = this.secondFont
    ctx.fillText(this.second, width / 2, top + this.firstLineHeight + this.secondLineHeight / 2)
  }
}

export class Section implements TicketElement {
  text: string
  paddingTop: number
  paddingBottom: number
  boxHeight: number

  constructor(text: string) {
    this.text = text
    this.paddingTop = 30
    this.paddingBottom = 20
    this.boxHeight = 48
  }

  height(): number {
    return this.paddingTop + this.boxHeight + this.paddingBottom
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    top += this.paddingTop
    ctx.fillStyle = 'black'
    ctx.fillRect(0, top, width, this.boxHeight)
    ctx.font = `bold 20px ${TITLE_FONT}`
    ctx.fillStyle = 'white'
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'left'
    ctx.fillText(this.text, 15, top + this.boxHeight / 2)
  }
}

type LabelOptions = {
  style?: string
  valueType?: 'string' | 'date' | 'currency'
  lineHeight?: number
  align?: CanvasTextAlign
  baseline?: CanvasTextBaseline
}

export class Label implements TicketElement {
  label: string | Date
  style: string
  valueType: 'string' | 'date' | 'currency'
  lineHeight: number
  align: CanvasTextAlign
  baseline: CanvasTextBaseline
  formattedLabel: string

  constructor(
    label: string | Date,
    { style, valueType, lineHeight, align, baseline }: LabelOptions = {}
  ) {
    this.label = label
    this.style = style || 'bold 16px'
    this.valueType = valueType || 'string'
    this.lineHeight = lineHeight || 22
    this.align = align || 'left'
    this.baseline = baseline || 'middle'
    if (this.valueType === 'date') {
      this.formattedLabel = formatDate(this.label)
    } else if (this.valueType === 'currency') {
      this.formattedLabel = formatCurrency(+this.label)
    } else {
      this.formattedLabel = String(this.label)
    }
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    ctx.font = `${this.style} ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textBaseline = this.baseline
    ctx.textAlign = this.align
    return this.lineHeight * splitLines(ctx, this.formattedLabel, width).length
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    const paddingTop = 20
    top += paddingTop
    ctx.font = `${this.style} ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textBaseline = this.baseline
    ctx.textAlign = this.align
    let left = 0
    if (this.align === 'center') {
      left = width / 2
    }
    wrapText(ctx, this.formattedLabel, left, top, width, {
      lineHeight: this.lineHeight,
      decreaseIndent: true
    })
  }
}

type InfoItemOptions = {
  valueType?: 'text' | 'string' | 'date' | 'currency' | 'dateWithoutTime'
  important?: boolean
  light?: boolean
  keepTogether?: boolean
  fontSize?: number
  // FIXME: seems to be used but not implemented
  style?: string
}

export class InfoItem implements TicketElement {
  label: string
  value: string | number | Date | undefined | null
  valueType: 'text' | 'string' | 'date' | 'currency' | 'dateWithoutTime'
  important: boolean
  labelFont: string
  fontSize: number
  lineHeight: number
  keepTogether: boolean
  formattedValue: string

  constructor(
    label: string,
    value: string | number | Date | undefined | null,
    { valueType, important, light, keepTogether, fontSize = 16 }: InfoItemOptions = {}
  ) {
    this.label = label + ':'
    this.value = value
    this.valueType = valueType || 'string'
    this.important = important || false
    this.labelFont = light ? `${fontSize}px ${BODY_FONT}` : `bold ${fontSize}px ${BODY_FONT}`
    this.fontSize = fontSize
    this.lineHeight = fontSize * 1.5
    this.keepTogether = !!keepTogether
    if (this.valueType === 'date') {
      this.formattedValue = formatDate(this.value as Date)
    } else if (this.valueType === 'currency') {
      this.formattedValue = formatCurrency(+this.value!)
    } else if (this.valueType === 'dateWithoutTime') {
      this.formattedValue = formatDateWithoutTime(this.value as Date)
    } else {
      this.formattedValue = String(this.value)
    }
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    if (this.value === undefined || this.value === null || this.value === '') return 0
    ctx.font = this.labelFont
    ctx.font = `${this.fontSize}px ${BODY_FONT}`
    ctx.textAlign = 'right'
    if (this.important) {
      ctx.font = `bold 18px ${BODY_FONT}`
    }
    let height: number
    if (!this.keepTogether) {
      const valueWidth = width - (ctx.measureText(this.label).width + 10)
      const linesLabel = splitLines(ctx, this.label, width)
      const linesValue = splitLines(ctx, this.formattedValue, width, valueWidth)
      height = this.lineHeight * (linesLabel.length + linesValue.length - 1)
    } else {
      const fullText = this.label + ' ' + this.formattedValue
      const fullTextWidth = width - (ctx.measureText(this.label).width + 10)
      const linesFullText = splitLines(ctx, fullText, width, fullTextWidth)
      height = this.lineHeight * linesFullText.length
    }
    return height
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    if (this.value === undefined || this.value === null || this.value === '') return
    top += this.lineHeight
    ctx.font = this.labelFont
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    const valueWidth = width - (ctx.measureText(this.label).width + 10)
    ctx.font = `${this.fontSize}px ${BODY_FONT}`
    if (this.important) {
      ctx.font = `bold ${this.fontSize * 1.2}px ${BODY_FONT}`
    }
    if (!this.keepTogether) {
      wrapText(ctx, this.label, 0, top, width, {
        lineHeight: this.fontSize * 1.5
      })
      ctx.textAlign = 'right'
      const lines = splitLines(ctx, this.label, width)
      top += this.lineHeight * (lines.length - 1)
      wrapText(ctx, this.formattedValue, width, top, width, {
        firstMaxWidth: valueWidth
      })
    } else {
      ctx.fillText(this.label, 0, top)
      wrapText(ctx, this.formattedValue, ctx.measureText(this.label).width + 5, top, width, {
        lineHeight: this.lineHeight,
        decreaseIndent: true,
        firstMaxWidth: valueWidth
      })
    }
  }
}

type DoubleInfoItemOptions = {
  firstLabelFont?: string
  secondLabelFont?: string
  secondType?: 'string' | 'date' | 'currency'
}

export class DoubleInfoItem implements TicketElement {
  firstLabel: string
  firstValue: string | number | undefined | null
  secondLabel: string
  secondValue: string | number | undefined | null
  secondType: 'string' | 'date' | 'currency'
  firstLabelFont: string
  secondLabelFont: string
  font: string
  lineHeight: number
  important?: boolean

  constructor(
    firstLabel: string,
    firstValue: string | number | undefined,
    secondLabel: string,
    secondValue: string | number | undefined,
    { firstLabelFont, secondLabelFont, secondType }: DoubleInfoItemOptions = {}
  ) {
    this.firstLabel = firstLabel + ':'
    this.firstValue = firstValue
    this.secondLabel = secondLabel + ':'
    this.secondValue = secondValue
    this.secondType = secondType || 'string'
    this.firstLabelFont = firstLabelFont || `bold 16px ${BODY_FONT}`
    this.secondLabelFont = secondLabelFont || `bold 16px ${BODY_FONT}`
    this.font = `16px ${BODY_FONT}`
    this.lineHeight = 25
    if (this.secondType === 'date') {
      this.secondValue = formatDate(this.secondValue)
    } else if (this.secondType === 'currency') {
      this.secondValue = formatCurrency(+this.secondValue!)
    }
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    if (!this.firstValue && !this.secondValue) {
      return 0
    }
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    ctx.font = this.firstLabelFont
    const fullFirstValue = this.firstLabel + ' ' + this.firstValue + ' '
    const fullSecondValue = this.secondLabel + ' ' + this.secondValue
    if (splitLines(ctx, fullFirstValue + fullSecondValue, width).length === 1) {
      // Both first item and second item fit in a line, total height will be one line
      return this.lineHeight * 1
    } else {
      // First and second items don't fit in one line, so total lines is the sum of the lines of each
      return (
        this.lineHeight *
        (splitLines(ctx, fullFirstValue, width).length +
          splitLines(ctx, fullSecondValue, width).length)
      )
    }
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    if (!this.firstValue && !this.secondValue) return
    top += this.lineHeight
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    const fullFirstValue = this.firstLabel + ' ' + this.firstValue + ' '
    const fullSecondValue = this.secondLabel + ' ' + this.secondValue
    ctx.font = this.firstLabelFont
    const bothItemsDontFitInOneLine =
      splitLines(ctx, fullFirstValue + fullSecondValue, width).length > 1
    if (bothItemsDontFitInOneLine) {
      ctx.fillText(this.firstLabel, 0, top)
      ctx.font = this.font
      const firstItemLines = wrapText(
        ctx,
        this.firstValue!,
        ctx.measureText(this.firstLabel).width + 5,
        top,
        width - (ctx.measureText(this.firstLabel).width + 10),
        {
          lineHeight: this.lineHeight,
          decreaseIndent: true
        }
      )
      ctx.font = this.secondLabelFont
      ctx.fillText(this.secondLabel, 0, top + this.lineHeight * firstItemLines)
      ctx.font = this.font
      wrapText(
        ctx,
        this.secondValue!,
        ctx.measureText(this.secondLabel).width + 5,
        top + this.lineHeight * firstItemLines,
        width - (ctx.measureText(this.secondLabel).width + 10),
        {
          lineHeight: this.lineHeight,
          decreaseIndent: true
        }
      )
    } else {
      if (this.firstValue) {
        ctx.font = this.firstLabelFont
        ctx.fillText(this.firstLabel, 0, top)
        ctx.font = this.font
        ctx.fillText(this.firstValue.toString(), ctx.measureText(this.firstLabel).width + 10, top)
        if (this.secondValue) {
          ctx.textAlign = 'right'
          ctx.fillText(this.secondValue.toString(), width, top)
          ctx.font = this.secondLabelFont
          ctx.fillText(
            this.secondLabel,
            width - (ctx.measureText(this.secondValue.toString()).width + 10),
            top
          )
        }
      } else {
        ctx.font = this.secondLabelFont
        ctx.fillText(this.secondLabel, 0, top)
        ctx.font = this.font
        ctx.fillText(
          this.secondValue!.toString(),
          ctx.measureText(this.secondLabel).width + 10,
          top
        )
      }
    }

    if (this.important) {
      ctx.font = `bold 18px ${BODY_FONT}`
    }
  }
}

type DoubleLabelOptions = {
  firstStyle?: string
  secondStyle?: string
  secondType?: 'string' | 'date' | 'currency' | ''
}

export class DoubleLabel implements TicketElement {
  first: string
  second: string
  firstStyle?: string
  secondStyle?: string
  secondType?: 'string' | 'date' | 'currency' | ''
  lineHeight: number
  formattedSecond: string
  important?: boolean

  constructor(
    first: string,
    second: string,
    { firstStyle, secondStyle, secondType }: DoubleLabelOptions = {}
  ) {
    this.first = first
    this.second = second
    this.firstStyle = firstStyle
    this.secondStyle = secondStyle
    this.secondType = secondType
    this.lineHeight = 30
    if (secondType === 'date') {
      this.formattedSecond = formatDate(this.second)
    } else if (secondType === 'currency') {
      this.formattedSecond = formatCurrency(+this.second)
    } else {
      this.formattedSecond = String(this.second)
    }
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    if (!this.first || !this.second) return 0
    ctx.font = this.firstStyle || `16px ${BODY_FONT}`
    const valueWidth = width - (ctx.measureText(this.first).width + 10)
    ctx.textAlign = 'right'
    return this.lineHeight * splitLines(ctx, this.formattedSecond, valueWidth).length
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    if (!this.first || !this.second) return
    top += this.lineHeight
    ctx.font = this.firstStyle || `16px ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    ctx.fillText(this.first, 0, top)
    const valueWidth = width - (ctx.measureText(this.first).width + 10)
    ctx.font = this.secondStyle || `16px ${BODY_FONT}`
    ctx.textAlign = 'right'
    if (this.important) {
      ctx.font = `bold 18px ${BODY_FONT}`
    }
    wrapText(ctx, this.formattedSecond, width, top, valueWidth)
  }
}

export class Tab implements TicketElement {
  code: string
  date: string
  total: string
  lineHeight: number

  constructor(code: string, date: string, total: number) {
    this.code = code
    this.date = formatDate(date)
    this.total = formatCurrency(total)
    this.lineHeight = 25
  }

  height() {
    return this.lineHeight
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number) {
    if (!this.code) return
    top += this.lineHeight
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    ctx.font = `bold 18px ${BODY_FONT}`
    ctx.fillText(this.code, 0, top)
    ctx.font = `16px ${BODY_FONT}`
    ctx.textAlign = 'center'
    ctx.fillText(this.date, width / 2, top)
    const valueWidth = width - (ctx.measureText(this.code).width + 10)
    ctx.textAlign = 'right'
    wrapText(ctx, this.total, width, top, valueWidth)
  }
}

export class Total implements TicketElement {
  label: string
  fontSize: number
  value: number
  lineHeight: number
  formattedValue: string
  paddingTop: number
  diners?: number | null
  isFullBill: boolean
  secondaryLabel: string

  constructor(value: number, diners?: number | null, type?: 'with-tips' | 'total' | null) {
    this.label = (type === 'with-tips' ? t('bill.total-without-tips') : t('bill.total')) + ':'
    this.fontSize = type === 'with-tips' ? 22 : 26
    this.value = value
    this.lineHeight = 25
    this.formattedValue = formatCurrency(this.value)
    this.paddingTop = 40
    this.diners = diners
    this.isFullBill = type === 'total'
    this.secondaryLabel = t('bill.total-per-person')
  }

  height(ctx: CanvasRenderingContext2D, width: number) {
    ctx.font = `bold ${this.fontSize}px ${BODY_FONT}`
    const valueWidth = width - (ctx.measureText(this.label).width + 10)
    ctx.textAlign = 'right'
    let height =
      this.lineHeight * splitLines(ctx, this.formattedValue, valueWidth).length + this.paddingTop
    if (this.diners && this.diners > 1 && this.isFullBill) height += this.lineHeight
    return height
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number) {
    if (!this.value) return
    top += this.paddingTop
    ctx.font = `bold ${this.fontSize}px ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    ctx.fillText(this.label, 0, top)
    let valueWidth = width - (ctx.measureText(this.label).width + 10)
    ctx.textAlign = 'right'
    wrapText(ctx, this.formattedValue, width, top, valueWidth)
    if (this.diners && this.diners > 1 && this.isFullBill) {
      this.secondaryLabel += ` (${this.diners}) :`
      top += splitLines(ctx, this.formattedValue, valueWidth).length * this.lineHeight
      ctx.font = `16px ${BODY_FONT}`
      ctx.textAlign = 'left'
      ctx.fillText(this.secondaryLabel, 0, top)
      ctx.textAlign = 'right'
      valueWidth = width - (ctx.measureText(this.secondaryLabel).width + 10)
      wrapText(ctx, formatCurrency(Math.ceil(this.value / this.diners)), width, top, valueWidth)
    }
  }
}

export class Separator implements TicketElement {
  padding: number
  lineHeight: number

  constructor({ size = 1, padding = 20 }: { size?: number; padding?: number } = {}) {
    this.padding = padding ?? 20
    this.lineHeight = size ?? 1
  }

  height(): number {
    return this.padding * 2 + this.lineHeight
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): number {
    ctx.fillStyle = 'black'
    ctx.fillRect(0, top + this.padding, width, this.lineHeight)
    return this.padding * 2 + this.lineHeight
  }
}

export class InputLine {
  size: number

  constructor({ size = 20 }: { size?: number } = {}) {
    this.size = size
  }

  height(): number {
    return this.size * 2 + 1
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): number {
    ctx.fillStyle = 'black'
    ctx.fillRect(0, top + this.size, width, 1)
    return this.size * 2 + 1
  }
}

export class DottedSeparator implements TicketElement {
  padding: number
  lineHeight: number

  constructor({ size = 1, padding = 20 }: { size?: number; padding?: number } = {}) {
    this.padding = padding ?? 20
    this.lineHeight = size ?? 1
  }

  height(): number {
    return this.padding * 2 + this.lineHeight
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): number {
    ctx.fillStyle = 'black'
    ctx.setLineDash([3, 3])
    ctx.moveTo(0, top + this.padding)
    ctx.lineTo(width, top + this.padding)
    ctx.stroke()
    ctx.setLineDash([])
    return this.padding * 2 + this.lineHeight
  }
}

export class EmptySeparator implements TicketElement {
  lineHeight: number

  constructor({ lineHeight }: { lineHeight?: number } = {}) {
    this.lineHeight = lineHeight ?? 22
  }

  height(): number {
    return this.lineHeight
  }

  render(): void {
    // No render, just space
  }
}

export class KitchenProduct implements TicketElement {
  product: KitchenProductFormatted | KitchenComboFormatted
  pickupType: string | undefined
  quantityWidth: number
  priceWidth: number
  lineHeight: number
  paddingBottom: number
  fontSize: number
  showQuantity: boolean

  constructor(
    product: KitchenProductFormatted | KitchenComboFormatted,
    pickupType?: string,
    { fontSize = 24, showQuantity = true } = {}
  ) {
    this.product = product
    this.pickupType = pickupType
    this.quantityWidth = 50
    this.priceWidth = 50
    this.lineHeight = fontSize
    this.paddingBottom = 10
    this.fontSize = fontSize
    this.showQuantity = showQuantity
    if (!showQuantity) this.quantityWidth = 0
  }

  productName() {
    return this.product.name
  }

  height(ctx: CanvasRenderingContext2D, width: number) {
    ctx.font = this.font({ bold: true })
    let quantityWidth = this.quantityWidth
    if (!this.product.quantity.current) {
      quantityWidth *= 2
    }
    const infoWidth = width - quantityWidth
    const nameLines = splitLines(ctx, this.productName(), infoWidth).length
    const modifierLines = this.modifierLines()
      .map(modifier => {
        if (modifier.modifier.added) {
          ctx.font = this.font({ bold: true, small: true })
        } else {
          ctx.font = this.font({ small: true })
        }
        return splitLines(ctx, modifier.text, infoWidth).length
      })
      .reduce((total, lines) => (total += lines), 0)
    const comments = this.product?.comments?.text || ''
    ctx.font = this.font({ small: true })
    const commentsLines = splitLines(ctx, comments, infoWidth).length
    return (nameLines + modifierLines + commentsLines) * this.lineHeight + this.paddingBottom
  }

  modifierLines() {
    return this.product.modifiers.map(modifier => {
      let modifierLine = '+ ' + modifier.name
      if (modifier.quantity! > 1) {
        modifierLine += ' x ' + modifier.quantity
      }
      return { text: modifierLine, modifier }
    })
  }

  font({ bold = false, small = false } = {}) {
    let fontSize = this.fontSize
    if (small) fontSize *= 0.8
    let font = `${fontSize}px ${BODY_FONT}`
    if (bold) font = 'bold ' + font
    return font
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number) {
    const paddingTop = 20
    top += paddingTop
    ctx.font = this.font({ bold: true })
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    let quantityWidth = this.quantityWidth
    if (this.showQuantity) {
      if (this.product.quantity.current) {
        ctx.fillText(this.product.quantity.current + 'x', 0, top)
      } else {
        ctx.fillRect(0, top - 13, quantityWidth, 2)
        ctx.font = this.font()
        ctx.fillText(this.product.quantity.old + 'x', 0, top)
        ctx.font = this.font({ bold: true })
        ctx.fillText(this.product.quantity.new + 'x', quantityWidth, top)
        quantityWidth *= 2
        ctx.font = this.font()
      }
    }
    ctx.textAlign = 'right'
    ctx.font = this.font()
    ctx.textAlign = 'left'
    const infoWidth = width - quantityWidth
    top +=
      wrapText(ctx, this.productName(), quantityWidth, top, infoWidth, {
        strike: this.product.deleted
      }) * this.lineHeight
    this.modifierLines().forEach(modifierLine => {
      if (modifierLine.modifier.added) {
        ctx.font = this.font({ bold: true, small: true })
      } else {
        ctx.font = this.font({ small: true })
      }
      top +=
        wrapText(ctx, modifierLine.text, quantityWidth, top, infoWidth, {
          strike: modifierLine.modifier.deleted || this.product.deleted
        }) * this.lineHeight
    })
    ctx.font = this.font({ small: true })
    top +=
      wrapText(ctx, this.product.comments.text, quantityWidth, top, infoWidth, {
        strike: this.product.deleted
      }) * this.lineHeight
  }
}

export class Product implements TicketElement {
  product: TabProduct
  pickupType: string | undefined
  quantityWidth: number
  priceWidth: number
  lineHeight: number
  paddingBottom: number

  constructor(product: TabProduct, pickupType?: string) {
    this.product = product
    this.pickupType = pickupType
    this.quantityWidth = 35
    this.priceWidth = 50
    this.lineHeight = 22
    this.paddingBottom = 10
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    const infoWidth = width - this.quantityWidth - this.priceWidth
    const nameLines = splitLines(ctx, this.product.name, infoWidth).length
    const modifierLines = this.modifierLines()
      .map((modifier: string) => splitLines(ctx, modifier, infoWidth).length)
      .reduce((total: number, lines: number) => (total += lines), 0)
    const comboProductLines = this.comboProductLines()
      .map((comboProduct: string) => splitLines(ctx, comboProduct, infoWidth).length)
      .reduce((total: number, lines: number) => (total += lines), 0)
    let commentsLines = 0
    if (this.pickupType) {
      commentsLines = splitLines(ctx, this.product.comments, infoWidth).length
    }
    const discountLines = this.product.discountType ? 1 : 0
    return (
      (nameLines + modifierLines + discountLines + comboProductLines + commentsLines) *
        this.lineHeight +
      this.paddingBottom
    )
  }

  modifierLines(modifiers?: TabModifier[], prefix = ''): string[] {
    return (modifiers || this.product.modifiers).map(modifier => {
      let modifierLine = prefix + '+ ' + modifier.name
      if (modifier.quantity! > 1) {
        modifierLine += ' x ' + modifier.quantity
      }
      return modifierLine
    })
  }

  comboProductLines(): string[] {
    return (this.product.comboProducts || []).flatMap((comboProduct: TabComboProduct) => {
      const prefix = '  '
      let comboProductLine = prefix + comboProduct.name
      if (comboProduct.quantity > 1) {
        comboProductLine += ' x ' + comboProduct.quantity
      }
      return [comboProductLine, ...this.modifierLines(comboProduct.modifiers, prefix)]
    })
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    const paddingTop = 20
    top += paddingTop
    ctx.font = `bold 16px ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textBaseline = 'bottom'
    ctx.textAlign = 'left'
    ctx.fillText(this.product.quantity + 'x', 0, top)
    ctx.textAlign = 'right'
    if (this.product.discountType) {
      ctx.font = `16px ${BODY_FONT}`
      const formattedPrice = formatCurrency(this.product.originalPrice)
      ctx.fillText(formattedPrice, width, top)
      const size = ctx.measureText(formattedPrice)
      ctx.fillRect(width - size.width, top - this.lineHeight / 2, size.width, 2)
      ctx.font = `bold 16px ${BODY_FONT}`
      ctx.fillText(formatCurrency(this.product.price), width, top + this.lineHeight)
    } else {
      ctx.fillText(formatCurrency(this.product.price), width, top)
    }
    ctx.font = `16px ${BODY_FONT}`
    ctx.textAlign = 'left'
    const infoWidth = width - this.quantityWidth - this.priceWidth
    top += wrapText(ctx, this.product.name, this.quantityWidth, top, infoWidth) * this.lineHeight
    if (this.product.discountType) {
      ctx.font = `bold 16px ${BODY_FONT}`
      let discountLabel = t('bill.discount')
      if (this.product.discountType === '2x1') {
        discountLabel += ' 2x1'
      } else if (this.product.discountType === 'percentage') {
        const amount = formatCurrency(this.product.originalPrice - this.product.price)
        discountLabel += ` ${this.product.discountAmount}% (-${amount})`
      } else {
        discountLabel += ' ' + formatCurrency(this.product.discountAmount!)
      }
      top += wrapText(ctx, discountLabel, this.quantityWidth, top, infoWidth) * this.lineHeight
    }
    ctx.font = `16px ${BODY_FONT}`
    this.modifierLines().forEach((modifier: string) => {
      top += wrapText(ctx, modifier, this.quantityWidth, top, infoWidth) * this.lineHeight
    })
    this.comboProductLines().forEach((comboProduct: string) => {
      top += wrapText(ctx, comboProduct, this.quantityWidth, top, infoWidth) * this.lineHeight
    })
    if (!!this.pickupType && this.product.comments) {
      top +=
        wrapText(ctx, this.product.comments, this.quantityWidth, top, infoWidth) * this.lineHeight
    }
  }
}

export class Box implements TicketElement {
  padding: number
  marginTop: number
  titleMarginBottom: number
  lineHeight: number
  title: string
  content?: string

  constructor(title: string, content?: string) {
    this.padding = 10
    this.marginTop = 20
    this.titleMarginBottom = 5
    this.lineHeight = 22
    this.title = title?.toUpperCase()
    this.content = content
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    if (!this.title && !this.content) return 0
    ctx.font = `bold 16px ${BODY_FONT}`
    ctx.textAlign = 'left'
    const messageWidth = width - this.padding
    const lines = splitLines(ctx, this.title, messageWidth).length
    ctx.font = `16px ${BODY_FONT}`
    const contentLines = splitLines(ctx, this.content, messageWidth).length
    return (
      this.marginTop +
      this.titleMarginBottom +
      this.padding * 2 +
      this.lineHeight * (lines + contentLines)
    )
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    ctx.fillStyle = 'black'
    ctx.font = `bold 16px ${BODY_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    top += this.marginTop
    const messageWidth = width - this.padding * 2
    const height =
      wrapText(ctx, this.title, width / 2, top + this.padding + this.lineHeight / 2, messageWidth) *
      this.lineHeight
    ctx.font = `14px ${BODY_FONT}`
    const contentHeight =
      wrapText(
        ctx,
        this.content,
        width / 2,
        top + height + this.titleMarginBottom + this.padding + this.lineHeight / 2,
        messageWidth
      ) * this.lineHeight
    ctx.strokeRect(
      0,
      top,
      width,
      height + contentHeight + this.titleMarginBottom + this.padding * 2
    )
  }
}

type Payment = {
  label: string
  amount: number
  tip?: number
  change?: number
}

export class PaymentsBox implements TicketElement {
  padding: number
  marginTop: number
  titleMarginBottom: number
  lineHeight: number
  title: string
  payments: Payment[]
  border: boolean
  important: boolean
  titleFont: string
  methodFont: string
  amountFont: string
  font: string

  constructor(
    title: string,
    payments: Payment[],
    {
      border = true,
      padding = 20,
      important = false
    }: { border?: boolean; padding?: number; important?: boolean } = {}
  ) {
    this.padding = padding
    this.marginTop = 20
    this.titleMarginBottom = 10
    this.lineHeight = 22
    this.title = important ? title.toUpperCase() : title
    this.payments = payments
    this.border = border
    this.important = important
    this.titleFont = important ? `bold 18px ${BODY_FONT}` : `18px ${BODY_FONT}`
    this.methodFont = important ? `20px ${BODY_FONT}` : `bold 14px ${BODY_FONT}`
    this.amountFont = important ? `bold 22px ${BODY_FONT}` : `14px ${BODY_FONT}`
    this.font = `14px ${BODY_FONT}`
  }

  height(ctx: CanvasRenderingContext2D, width: number): number {
    ctx.font = this.titleFont
    ctx.textAlign = this.important ? 'center' : 'left'
    const messageWidth = width - this.padding
    const lines = splitLines(ctx, this.title, messageWidth).length
    ctx.font = this.font
    const paymentLines = this.payments.reduce((acc, payment) => {
      return acc + (payment.change || (payment.tip && this.payments.length > 1) ? 1 : 0) + 1
    }, 0)
    return (
      this.marginTop +
      this.titleMarginBottom +
      this.padding * 2 +
      this.lineHeight * (lines + paymentLines)
    )
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    ctx.textBaseline = 'middle'
    ctx.font = this.titleFont
    ctx.textAlign = this.important ? 'center' : 'left'
    top += this.marginTop
    const messageWidth = width - this.padding * 2
    const height =
      wrapText(
        ctx,
        this.title,
        this.important ? width / 2 : this.padding,
        top + this.padding + this.lineHeight / 2,
        messageWidth
      ) * this.lineHeight
    ctx.font = this.font
    const firstPaymentPosition =
      top + height + this.titleMarginBottom + this.padding + this.lineHeight / 2
    let lineCounter = 0
    for (let i = 0; i < this.payments.length; ++i) {
      ctx.font = this.methodFont
      ctx.textAlign = 'left'
      ctx.fillText(
        this.payments[i].label.charAt(0).toUpperCase() + this.payments[i].label.slice(1) + ':',
        this.padding,
        firstPaymentPosition + lineCounter * this.lineHeight
      )
      ctx.font = this.amountFont
      ctx.textAlign = 'right'
      ctx.fillText(
        formatCurrency(
          this.payments[i].amount - (this.payments[i].tip || 0) + (this.payments[i].change || 0)
        ),
        width - this.padding,
        firstPaymentPosition + lineCounter * this.lineHeight
      )
      if (this.payments[i].change || (this.payments[i].tip && this.payments.length > 1)) {
        lineCounter++
        ctx.font = `14px ${BODY_FONT}`
        ctx.textAlign = 'left'
        ctx.fillText(
          this.payments[i].change ? t('checkout.change') : t('checkout.tip'),
          this.padding + 10,
          firstPaymentPosition + lineCounter * this.lineHeight
        )
        ctx.font = this.amountFont
        ctx.textAlign = 'right'
        ctx.fillText(
          formatCurrency(this.payments[i].change || this.payments[i].tip!),
          width - this.padding,
          firstPaymentPosition + lineCounter * this.lineHeight
        )
      }
      lineCounter++
    }
    const paymentsHeight = this.payments.length * this.lineHeight
    if (this.border) {
      ctx.strokeRect(
        0,
        top,
        width,
        height + paymentsHeight + this.titleMarginBottom + this.padding * 2
      )
    }
  }
}

export class If implements TicketElement {
  condition: boolean
  trueComponent: TicketElement
  falseComponent?: TicketElement

  constructor(condition: unknown, trueComponent: TicketElement, falseComponent?: TicketElement) {
    this.condition = !!condition
    this.trueComponent = trueComponent
    this.falseComponent = falseComponent
  }

  height(ctx: CanvasRenderingContext2D, width: number) {
    if (this.condition) {
      return this.trueComponent.height(ctx, width)
    } else if (this.falseComponent) {
      return this.falseComponent.height(ctx, width)
    }
    return 0
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number) {
    if (this.condition) {
      this.trueComponent.render(top, ctx, width)
    } else if (this.falseComponent) {
      this.falseComponent.render(top, ctx, width)
    }
  }
}

export class Barcode implements TicketElement {
  code: string
  barcodeHeight: number
  paddingTop: number

  constructor(code: string) {
    this.code = code
    this.barcodeHeight = 80
    this.paddingTop = 25
  }

  height(): number {
    return this.barcodeHeight + this.paddingTop
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    top += this.paddingTop
    const canvas = document.createElement('canvas')
    code128(canvas, {
      bcid: 'code128',
      text: this.code,
      scaleX: 1,
      scaleY: 1,
      width,
      height: this.barcodeHeight
    })
    ctx.drawImage(canvas, 0, top, width, this.barcodeHeight)
  }
}

export class QR implements TicketElement {
  code: string
  barcodeHeight: number

  constructor(code: string, { height }: { height?: number } = {}) {
    this.code = code
    this.barcodeHeight = height || 120
  }

  height(): number {
    return this.barcodeHeight
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    const canvas = document.createElement('canvas')
    qrcode(canvas, {
      bcid: 'qrcode',
      text: this.code,
      scaleX: 1,
      scaleY: 1,
      width: this.barcodeHeight,
      height: this.barcodeHeight
    })
    const paddingLeft = (width - this.barcodeHeight) / 2
    ctx.drawImage(canvas, paddingLeft, top, this.barcodeHeight, this.barcodeHeight)
  }
}

type TillMethod = {
  endAmount: number
  expectedEndAmount: number
}

export class TillBreakdown implements TicketElement {
  methods: Record<string, TillMethod>
  lineHeight: number
  separatorPadding: number
  topPadding: number
  totalExpectedEndAmount: number
  totalEndAmount: number

  constructor(methods: Record<string, TillMethod>) {
    this.methods = methods
    this.lineHeight = 22
    this.separatorPadding = 20
    this.topPadding = 30

    this.totalExpectedEndAmount = Object.values(methods).reduce((total, shift) => {
      total += shift.expectedEndAmount
      return total
    }, 0)

    this.totalEndAmount = Object.values(methods).reduce((total, shift) => {
      total += shift.endAmount
      return total
    }, 0)
  }

  height(): number {
    return (
      this.lineHeight * Object.keys(this.methods).length +
      this.lineHeight +
      this.separatorPadding +
      this.topPadding
    )
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    top += this.topPadding
    ctx.font = `bold 12px ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textAlign = 'right'
    ctx.fillText(t('report.difference'), width, top)
    ctx.fillText(t('report.declared'), width * 0.75, top)
    ctx.fillText(t('report.calculated'), width * 0.5, top)
    Object.keys(this.methods).forEach(method => {
      top += this.lineHeight
      ctx.font = `12px ${BODY_FONT}`
      const difference = this.methods[method].endAmount - this.methods[method].expectedEndAmount
      ctx.textAlign = 'right'
      ctx.fillText(formatCurrency(difference), width, top)
      ctx.fillText(formatCurrency(this.methods[method].endAmount), width * 0.75, top)
      ctx.fillText(formatCurrency(this.methods[method].expectedEndAmount), width * 0.5, top)
      ctx.textAlign = 'left'
      ctx.font = `bold 12px ${BODY_FONT}`
      ctx.fillText(method.charAt(0).toUpperCase() + method.slice(1), 0, top)
    })
    ctx.fillStyle = 'black'
    ctx.font = `bold 12px ${BODY_FONT}`
    top += this.separatorPadding
    ctx.fillRect(0, top, width, 1)
    top += this.lineHeight
    ctx.fillText(t('report.total') + ':', 0, top)
    ctx.textAlign = 'right'
    ctx.fillText(formatCurrency(this.totalEndAmount - this.totalExpectedEndAmount), width, top)
    ctx.fillText(formatCurrency(this.totalEndAmount), width * 0.75, top)
    ctx.fillText(formatCurrency(this.totalExpectedEndAmount), width * 0.5, top)
  }
}

type TaxRate = {
  total: number
  tax: number
  taxableBase: number
  taxPercentage: number
}

export class TaxesBreakdown implements TicketElement {
  taxRates: TaxRate[]
  taxLabel: string
  lineHeight: number
  separatorPadding: number
  topPadding: number

  constructor(taxRates: TaxRate[], taxLabel: string) {
    this.taxRates = taxRates
    this.taxLabel = taxLabel
    this.lineHeight = 22
    this.separatorPadding = 20
    this.topPadding = 10
  }

  height(): number {
    return this.lineHeight * this.taxRates.length + this.lineHeight + this.topPadding
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    top += this.topPadding
    ctx.font = `12px ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textAlign = 'right'
    ctx.fillText(t('report.total'), width, top)
    ctx.fillText(this.taxLabel, width * 0.75, top)
    ctx.fillText(t('report.subtotal'), width * 0.5, top)
    this.taxRates.forEach((taxRate: TaxRate) => {
      top += this.lineHeight
      ctx.textAlign = 'right'
      ctx.font = `bold 16px ${BODY_FONT}`
      ctx.fillText(formatCurrency(taxRate.total), width, top)
      ctx.font = `16px ${BODY_FONT}`
      ctx.fillText(formatCurrency(taxRate.tax), width * 0.75, top)
      ctx.fillText(formatCurrency(taxRate.taxableBase), width * 0.5, top)
      ctx.textAlign = 'left'
      ctx.fillText(`${this.taxLabel} (${taxRate.taxPercentage}%):`, 0, top)
    })
  }
}

type ProductStock = {
  name: string
  initial: number | string
  current: number | string
  toRestock: number | string
}

export class ProductsStockBreakdown implements TicketElement {
  products: ProductStock[]
  lineHeight: number
  topPadding: number
  fontSize: number
  extraSpaceAfterHeader: number

  constructor(products: ProductStock[]) {
    this.products = products
    this.lineHeight = 26
    this.topPadding = 10
    this.fontSize = 12
    this.extraSpaceAfterHeader = 10
  }

  height(): number {
    const headerRowHeight = this.lineHeight
    const productsHeight = this.lineHeight * this.products.length
    return this.topPadding + headerRowHeight + productsHeight + this.extraSpaceAfterHeader
  }

  private truncateText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    const ellipsis = '...'
    const ellipsisWidth = ctx.measureText(ellipsis).width

    if (ctx.measureText(text).width <= maxWidth) {
      return text
    }

    let truncatedText = text
    while (truncatedText.length > 0) {
      truncatedText = truncatedText.substring(0, truncatedText.length - 1)
      const textWidth = ctx.measureText(truncatedText).width
      if (textWidth + ellipsisWidth <= maxWidth) {
        return truncatedText + ellipsis
      }
    }
    return ellipsis
  }

  render(top: number, ctx: CanvasRenderingContext2D, totalWidth: number): void {
    top += this.topPadding
    ctx.textBaseline = 'top'

    const columnWeights = {
      name: 3.2,
      initial: 1,
      current: 1,
      toRestock: 1
    }
    const totalWeight = Object.values(columnWeights).reduce((sum, weight) => sum + weight, 0)

    const horizontalPadding = 5
    const effectiveWidth = totalWidth - horizontalPadding * 2

    const unitWidth = effectiveWidth / totalWeight

    const columnWidths = {
      name: unitWidth * columnWeights.name,
      initial: unitWidth * columnWeights.initial,
      current: unitWidth * columnWeights.current,
      toRestock: unitWidth * columnWeights.toRestock
    }

    const columnCenters = {
      name: horizontalPadding + columnWidths.name / 2,
      initial: horizontalPadding + columnWidths.name + columnWidths.initial / 2,
      current:
        horizontalPadding + columnWidths.name + columnWidths.initial + columnWidths.current / 2,
      toRestock:
        horizontalPadding +
        columnWidths.name +
        columnWidths.initial +
        columnWidths.current +
        columnWidths.toRestock / 2
    }

    const headerY = top
    ctx.fillStyle = 'black'
    ctx.fillRect(0, headerY, totalWidth, this.lineHeight)

    ctx.fillStyle = 'white'
    ctx.font = `bold ${this.fontSize}px ${BODY_FONT}`

    const textVerticalOffset = (this.lineHeight - this.fontSize) / 2

    ctx.textAlign = 'left'
    ctx.fillText(t('report.product'), horizontalPadding, headerY + textVerticalOffset)

    ctx.textAlign = 'center'
    ctx.fillText(
      t('report.stock.initial-short'),
      columnCenters.initial,
      headerY + textVerticalOffset
    )
    ctx.fillText(
      t('report.stock.current-short'),
      columnCenters.current,
      headerY + textVerticalOffset
    )
    ctx.fillText(
      t('report.stock.to-restock-short'),
      columnCenters.toRestock - horizontalPadding,
      headerY + textVerticalOffset
    )

    top += this.lineHeight
    top += this.extraSpaceAfterHeader

    ctx.fillStyle = 'black'
    ctx.font = `${this.fontSize}px ${BODY_FONT}`

    const maxNameWidth = columnWidths.name - horizontalPadding

    this.products.forEach((product: ProductStock, index: number) => {
      const productName = this.truncateText(ctx, product.name, maxNameWidth)
      const productY = top + (this.lineHeight - this.fontSize) / 2

      ctx.textAlign = 'left'
      ctx.fillText(productName, horizontalPadding, productY)

      ctx.textAlign = 'center'
      ctx.fillText(product.initial.toString(), columnCenters.initial, productY)
      ctx.fillText(product.current.toString(), columnCenters.current, productY)
      ctx.fillText(
        product.toRestock.toString(),
        columnCenters.toRestock - horizontalPadding,
        productY
      )

      if (index < this.products.length - 1) {
        const lineY = top + this.lineHeight
        ctx.save()
        ctx.setLineDash([2, 3])
        ctx.beginPath()
        ctx.moveTo(0, lineY)
        ctx.lineTo(totalWidth, lineY)
        ctx.stroke()
        ctx.restore()
      }

      top += this.lineHeight
    })
  }
}

type Movement = {
  diff: number
  creationTime: string | number | Date
  tableName?: string
  tabCode?: string
}

export class MovementsBreakdown implements TicketElement {
  movements: Movement[]
  lineHeight: number
  separatorPadding: number
  topPadding: number
  totalEndAmount: number

  constructor(movements: Movement[]) {
    this.movements = movements
    this.lineHeight = 22
    this.separatorPadding = 20
    this.topPadding = 30
    this.totalEndAmount = movements.reduce((total, movement) => {
      total += movement.diff
      return total
    }, 0)
  }

  height(): number {
    return (
      this.lineHeight * this.movements.length +
      this.lineHeight +
      this.separatorPadding +
      this.topPadding
    )
  }

  render(top: number, ctx: CanvasRenderingContext2D, width: number): void {
    ctx.font = `bold 12px ${BODY_FONT}`
    ctx.fillStyle = 'black'
    ctx.textAlign = 'right'
    ctx.fillText(t('movements-report.balance'), width, top)
    this.movements.forEach((movement: Movement) => {
      ctx.textAlign = 'right'
      const inOrOut =
        movement.diff < 0 ? t('movements-report.pay-out') : t('movements-report.pay-in')
      top += this.lineHeight

      ctx.font = `12px ${BODY_FONT}`
      ctx.fillText(formatCurrency(movement.diff), width, top)
      ctx.fillText(format(new Date(movement.creationTime), 'HH:mm'), width * 0.5, top)
      ctx.textAlign = 'left'
      ctx.font = `bold 12px ${BODY_FONT}`
      ctx.fillText(movement.tableName || movement.tabCode || inOrOut, 0, top)
    })
    ctx.fillStyle = 'black'
    ctx.font = `bold 12px ${BODY_FONT}`
    top += this.separatorPadding
    ctx.fillRect(0, top, width, 1)
    top += this.lineHeight
    ctx.fillText(t('report.total') + ':', 0, top)
    ctx.textAlign = 'right'
    ctx.fillText(formatCurrency(this.totalEndAmount), width, top)
  }
}
